/**
 * File responsible for the content when clicking `Allocate to invoice` in credits management pages.
 */

import { Button, Col, DatePicker, Form, Modal, Row } from 'antd';
import { isEmpty, map, get } from 'lodash';
import React, { lazy, Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
    dateFormatDDMMMYYYYSpace,
    dateFormatYYYYMMDDDash,
} from '../../constants/dateFormats';
import { getPopoverContainer } from '../../utils/commonFunctions';
import { withNumberFormatHandler } from '../common/NumberFormatHandler';
import moment from 'moment-timezone';
import { withDateFormatHandler } from '../common/DateFormatHandler';

const ModalWithSpinner = lazy(() => import('../common/ModalWithSpinner'));

const { Item: FormItem } = Form;

interface IProps {
    readonly containerRef?: any;
    readonly visible: boolean;
    readonly closePanel?: (refetchList?: boolean) => void;
    readonly form?: any;
    readonly dispatchAction: (payload: any) => void;
    readonly formatDateToDateObjectUTC: (
        date: any,
        fromFormat?: string | null
    ) => any;
}

const CreditReconcileReportPanel: React.FC<IProps> = ({
    containerRef,
    visible,
    closePanel,
    form,
    dispatchAction,
    formatDateToDateObjectUTC,
}: IProps) => {
    const dispatch = useDispatch();
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const {
        getFieldDecorator,
        validateFields,
        resetFields,
        getFieldValue,
    } = form;

    /**
     * Function called when `Cancel` button is clicked inside Reconcile report panel.
     */
    const handleClosePanel = () => {
        if (closePanel) closePanel();
    };

    /**
     * Function that listens if panel is closed.
     * If closed, the form fields and values will reset.
     */
    const listenForClosingPanel = () => {
        if (!visible) {
            resetFields();
        }
    };

    useEffect(listenForClosingPanel, [visible]);

    /**
     * Function called when submitting the form.
     */
    const handleSubmitForm = () => {
        validateFields((err: any, values: any) => {
            if (!err) {
                const startDateLocal =
                    moment(get(values, 'StartDate')).format(
                        dateFormatYYYYMMDDDash
                    ) + 'T00:00:00';
                const endDateLocal =
                    moment(get(values, 'EndDate')).format(
                        dateFormatYYYYMMDDDash
                    ) + 'T23:59:59';

                const startDate = formatDateToDateObjectUTC(startDateLocal);
                const endDate = formatDateToDateObjectUTC(endDateLocal);

                if (moment(endDate).isBefore(moment(startDate))) {
                    Modal.error({
                        title: 'Error',
                        content: (
                            <div>
                                <b>Start date</b> should not be greater than{' '}
                                <b>End date</b>!
                            </div>
                        ),
                    });
                } else {
                    setSubmitLoading(true);
                    dispatch(
                        dispatchAction({
                            startDate,
                            endDate,
                            callback: reconcileReportResponseModal,
                        })
                    );
                }
            }
        });
    };

    /**
     * Function responsible for showing the response modal after reconciling the report.
     * @param param0 - object with success indicator and error message from api (if there's any)
     */
    const reconcileReportResponseModal = ({
        IsSuccess,
        Messages,
    }: {
        IsSuccess: boolean;
        Messages: string[] | undefined;
    }) => {
        setSubmitLoading(false);
        if (IsSuccess) {
            Modal.success({
                title: 'Success',
                content:
                    'Report generated successfully. You will be notified once it is finished together with the download link!',
                onOk: () => {
                    if (closePanel) closePanel(true);
                },
                getContainer: () => getPopoverContainer(containerRef),
            });
        } else {
            let errorMessageContent: any = `Failed to generate the report!`;
            if (!isEmpty(Messages)) {
                errorMessageContent = map(
                    Messages,
                    (error: string, index: number) => (
                        <div key={index}>{error}</div>
                    )
                );
            }

            Modal.error({
                title: 'Error',
                content: errorMessageContent,
                getContainer: () => getPopoverContainer(containerRef),
            });
        }
    };

    /**
     * Function that restricts the dates that can be selected by end date
     * @param dateValue
     */
    const restrictEndDate = (dateValue: any) => {
        return moment(moment(dateValue)).isBefore(
            moment(moment(getFieldValue('StartDate')))
        );
    };

    /**
     * Function responsible for populating the panel content.
     * Form fields.
     */
    const populatePanelContent = () => {
        const startDateUsed = moment().subtract(7, 'days');
        return (
            <Form className="form-inline-mb-0" labelCol={{ span: 8 }}>
                <Row>
                    <Col className="mb-10">
                        <FormItem label="Start date">
                            {getFieldDecorator('StartDate', {
                                initialValue: moment(startDateUsed),
                                rules: [
                                    {
                                        required: true,
                                        message: 'Start date required!',
                                    },
                                ],
                            })(
                                <DatePicker
                                    format={dateFormatDDMMMYYYYSpace}
                                    placeholder="Start"
                                    style={{ width: 300 }}
                                />
                            )}
                        </FormItem>
                    </Col>
                    <Col>
                        <FormItem label="End date">
                            {getFieldDecorator('EndDate', {
                                initialValue: moment(),
                                rules: [
                                    {
                                        required: true,
                                        message: 'End date required!',
                                    },
                                ],
                            })(
                                <DatePicker
                                    format={dateFormatDDMMMYYYYSpace}
                                    placeholder="End"
                                    disabledDate={restrictEndDate}
                                    style={{ width: 300 }}
                                />
                            )}
                        </FormItem>
                    </Col>
                </Row>
            </Form>
        );
    };

    return (
        <Row>
            <Col>
                <div>{populatePanelContent()}</div>
                <br />
                <Row>
                    <Col className="ta-right" span={24}>
                        <Button
                            className="mr-8"
                            type="primary"
                            onClick={handleSubmitForm}
                        >
                            Download
                        </Button>
                        <Button onClick={handleClosePanel}>Cancel</Button>
                    </Col>
                </Row>
            </Col>
            {submitLoading && (
                <Suspense fallback={null}>
                    <ModalWithSpinner
                        modalTitle="Generating report"
                        modalVisible={submitLoading}
                        displayMessage="Please wait while we start the data extraction process. . ."
                        containerRef={containerRef}
                    />
                </Suspense>
            )}
        </Row>
    );
};

const CreditReconcileReportPanelForm = Form.create({
    name: 'reconcile-report-panel-form',
})(CreditReconcileReportPanel);

export default withRouter(
    withNumberFormatHandler(
        withDateFormatHandler(CreditReconcileReportPanelForm)
    )
);
